import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginComponent from './components/LoginComponent';
import NGORegistration from './components/Register';
import Dashboard from './pages/Dashboard';
import AppHeader from './components/Header';
import NewNOCRequest, { EditApplicationFlow } from './pages/TankSelection';
import NOCDetails from './pages/NocDetails';
import WorkflowPage from './pages/WorkflowPage';
import StateDashboard from './pages/StateDashboard';
import DepartmentUserMaster from './pages/DepartmentUserMaster';
import HomePage from './pages/HomePage';
import LandingNavbar from './components/LandingNavbar';
import LandingPageFooter from './components/LandingFooter';
import ApplicationWorkflow from './pages/ApplicationWorkflow';
import DocumentsPage from './pages/Documents';
import GuidelinesPage from './pages/GuideLines';
import FAQPage from './pages/FAQ';
import GalleryPage from './pages/Gallery';
import ContactPage from './pages/ContactUs';

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Pen-test remediation — ProtectedLayout is the guard for every
// authenticated route. It:
//   1. Waits for AuthContext to finish hydrating from /auth/me.
//   2. Redirects to /login if the user is not authenticated.
//   3. Re-checks on every `pageshow` (fires when the browser restores from
//      the back-forward cache) so a signed-out user can never see a
//      cached authenticated page.
const ProtectedLayout = ({ children }) => {
  const { isReady, isAuthenticated, role, refresh } = useAuth();

  useEffect(() => {
    // If the browser restores the page from bfcache after logout, re-check
    // auth against the server.
    const onPageShow = (e) => { if (e.persisted) refresh(); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [refresh]);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }
  if (!isAuthenticated) {
    window.location.replace('/login');
    return null;
  }
  // STATE users are read-only, dashboard-only.
  if (role === 'state' && window.location.pathname !== '/state-dashboard') {
    window.location.replace('/state-dashboard');
    return null;
  }
  // DEPT_ADMIN users get the department dashboard by default, plus access
  // to the officer master and profile pages.
  const deptAdminAllowed = [
    '/dept-admin-dashboard',
    '/admin/department-users',
  ];
  if (role === 'dept_admin' && !deptAdminAllowed.includes(window.location.pathname)) {
    window.location.replace('/dept-admin-dashboard');
    return null;
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      {children}
    </div>
  );
};

// Layout component for auth pages (no header)
const AuthLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      {children}
    </div>
  );
};

// Layout component for landing page (with landing navbar and footer)
const LandingLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      <LandingNavbar />
      {children}
      <LandingPageFooter />
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
    <Router>
        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <div className="App min-h-screen">
       
        <Routes>
          {/* Landing page - with landing navbar */}
          <Route 
            path="/" 
            element={
              <LandingLayout>
                <HomePage />
              </LandingLayout>
            } 
          />
            <Route 
            path="/contact" 
            element={
              <LandingLayout>
                <ContactPage />
              </LandingLayout>
            } 
          />
              <Route 
            path="/guidelines" 
            element={
              <LandingLayout>
                <GuidelinesPage/>
              </LandingLayout>
            } 
          />
                     <Route 
            path="/gallery" 
            element={
              <LandingLayout>
                <GalleryPage/>
              </LandingLayout>
            } 
          />
             <Route 
            path="/documents" 
            element={
              <LandingLayout>
                <DocumentsPage />
              </LandingLayout>
            } 
          />
                <Route 
            path="/faq" 
            element={
              <LandingLayout>
                <FAQPage />
              </LandingLayout>
            } 
          />
                 <Route 
            path="/application-workflow" 
            element={
              <LandingLayout>
                <ApplicationWorkflow/>
              </LandingLayout>
            } 
          />
          {/* Auth routes - no header */}
          <Route 
            path="/login" 
            element={
              <AuthLayout>
                <LoginComponent />
              </AuthLayout>
            } 
          />
          <Route 
            path="/register" 
            element={
              <AuthLayout>
                <NGORegistration />
              </AuthLayout>
            } 
          />
                            
          {/* Protected routes - with header */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedLayout>
                <Dashboard />
              </ProtectedLayout>
            } 
          />
                            
          <Route 
            path="/workflow/:id" 
            element={
              <ProtectedLayout>
                <WorkflowPage />
              </ProtectedLayout>
            } 
          />
     
          <Route
            path="/new-noc-request"
            element={
              <ProtectedLayout>
                <NewNOCRequest />
              </ProtectedLayout>
            }
          />
          <Route
            path="/edit-application/:id"
            element={
              <ProtectedLayout>
                <EditApplicationFlow />
              </ProtectedLayout>
            }
          />
          <Route
            path="/noc-details"
            element={
              <ProtectedLayout>
                <NOCDetails />
              </ProtectedLayout>
            }
          />
          <Route
            path="/state-dashboard"
            element={
              <ProtectedLayout>
                <StateDashboard scope="state" />
              </ProtectedLayout>
            }
          />
          <Route
            path="/collector-dashboard"
            element={
              <ProtectedLayout>
                <StateDashboard scope="district" />
              </ProtectedLayout>
            }
          />
          <Route
            path="/dept-admin-dashboard"
            element={
              <ProtectedLayout>
                <StateDashboard scope="department" />
              </ProtectedLayout>
            }
          />
          <Route
            path="/admin/department-users"
            element={
              <ProtectedLayout>
                <DepartmentUserMaster />
              </ProtectedLayout>
            }
          />
        </Routes>
      </div>
    </Router>
    </AuthProvider>
  );
};

export default App;